import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "wouter";
import { AppShell } from "./AppShell";
import { AudienceEditor } from "./components/AudienceEditor";
import { TestOutbound } from "./components/TestOutbound";
import { apiFetch, fmtDateTime, fmtPhone } from "./lib/api";

// ---------------------------------------------------------------------------
// Section navigation. Add a new entity panel by appending to SECTIONS and
// rendering a case below.
// ---------------------------------------------------------------------------
type Section =
  | "inbound"
  | "outbound"
  | "agent_desk"
  | "model"
  | "flows"
  | "customers"
  | "offers"
  | "about";

// Two top-level groups: CONFIGURE (how inbound/outbound/agent-desk run) and
// BUILDING BLOCKS (the data that orchestration references). About sits on
// its own.
const SECTIONS: { value: Section; group?: string; label: string }[] = [
  { value: "inbound",    group: "Configure",       label: "Inbound" },
  { value: "outbound",   group: "Configure",       label: "Outbound" },
  { value: "agent_desk", group: "Configure",       label: "Agent Desk" },
  { value: "model",      group: "Building blocks", label: "Model" },
  { value: "flows",      group: "Building blocks", label: "Flows" },
  { value: "customers",  group: "Building blocks", label: "Subscribers" },
  { value: "offers",     group: "Building blocks", label: "Offers" },
  { value: "about",                                label: "About" },
];

const DEFAULT_SECTION: Section = "inbound";

function isSection(s: string | undefined): s is Section {
  return (
    s === "inbound" ||
    s === "outbound" ||
    s === "agent_desk" ||
    s === "model" ||
    s === "flows" ||
    s === "customers" ||
    s === "offers" ||
    s === "about"
  );
}

export function Settings({ section }: { section?: string } = {}) {
  const active: Section = isSection(section) ? section : DEFAULT_SECTION;
  const heading = SECTIONS.find((s) => s.value === active)?.label ?? "Settings";

  return (
    <AppShell secondaryNav={<SettingsNav active={active} />}>
      <main id="settings-main" className="settings-main">
        <div id="settings-inner" className="settings-inner">
          <header id="settings-header" className="settings-header">
            <h1>{heading}</h1>
          </header>

          {active === "inbound" && <InboundConfigPanel />}
          {active === "outbound" && <OutboundConfigPanel />}
          {active === "agent_desk" && <AgentDeskPanel />}
          {active === "model" && <ModelPanel />}
          {active === "flows" && <FlowsPanel />}
          {active === "customers" && <CustomersPanel />}
          {active === "offers" && <OffersPanel />}
          {active === "about" && <AboutPanel />}
        </div>
      </main>
    </AppShell>
  );
}

function SettingsNav({ active }: { active: Section }) {
  let lastGroup: string | undefined;
  return (
    <nav id="settings-nav" className="settings-nav">
      {SECTIONS.map((s) => {
        const newGroup = s.group && s.group !== lastGroup;
        lastGroup = s.group;
        return (
          <Fragment key={s.value}>
            {newGroup && <div className="settings-nav-group">{s.group}</div>}
            <Link
              id={`settings-nav-link-${s.value}`}
              href={`/settings/${s.value}`}
              className={`settings-nav-item ${active === s.value ? "is-active" : ""}`}
            >
              {s.label}
            </Link>
          </Fragment>
        );
      })}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Model panel — Realtime mode shows one provider per row; Pipeline mode
// breaks down into the three components (STT/LLM/TTS) that compose the
// chained voice stack. Each component is independently testable; live
// HTTP probes hit each provider's auth-listing endpoint.
// ---------------------------------------------------------------------------
type Provider = "openai" | "gemini" | "chained";

/** Identifier sent to /api/settings/test-provider/:name — also doubles as
 *  the React key for each row. Distinct from the activation Provider. */
type TestName =
  | "openai_realtime"
  | "gemini"
  | "deepgram"
  | "openai_chat"
  | "cartesia";

/** Settings keys whose values come from a per-provider model dropdown. */
type ModelKey =
  | "model_openai_realtime"
  | "model_gemini"
  | "model_chained_stt"
  | "model_chained_llm"
  | "voice_chained_tts";

type RealtimeRow = {
  testName: TestName;
  /** Which Provider value to PUT when this row is activated. */
  activateAs: Provider;
  label: string;
  sub: string;
  requires: string[];
  modelKey: ModelKey;
};

type PipelineRow = {
  testName: TestName;
  /** "STT" / "LLM" / "TTS" stage chip on the row. */
  stage: "STT" | "LLM" | "TTS";
  label: string;
  requires: string[];
  modelKey: ModelKey;
  /** Some rows are voices, not models. Drives the dropdown label. */
  modelLabel: "Model" | "Voice";
};

const REALTIME_ROWS: RealtimeRow[] = [
  {
    testName: "openai_realtime",
    activateAs: "openai",
    label: "OpenAI Realtime",
    sub: "Single multimodal service · STT + LLM + TTS in one node",
    requires: ["OPENAI_API_KEY"],
    modelKey: "model_openai_realtime",
  },
  {
    testName: "gemini",
    activateAs: "gemini",
    label: "Google Gemini Live",
    sub: "Single multimodal service · native-audio model",
    requires: ["GOOGLE_API_KEY (or GEMINI_API_KEY)"],
    modelKey: "model_gemini",
  },
];

const PIPELINE_ROWS: PipelineRow[] = [
  { testName: "deepgram",    stage: "STT", label: "Deepgram",  requires: ["DEEPGRAM_API_KEY"], modelKey: "model_chained_stt", modelLabel: "Model" },
  { testName: "openai_chat", stage: "LLM", label: "OpenAI",    requires: ["OPENAI_API_KEY"],   modelKey: "model_chained_llm", modelLabel: "Model" },
  { testName: "cartesia",    stage: "TTS", label: "Cartesia",  requires: ["CARTESIA_API_KEY"], modelKey: "voice_chained_tts", modelLabel: "Voice" },
];

type ModelOption = {
  value: string;
  label: string;
  is_default?: boolean;
  hint?: string;
};

type TestResult =
  | { state: "idle" }
  | { state: "running" }
  | { state: "ok"; model?: string | undefined; latencyMs?: number | undefined; checked: string[] }
  | { state: "missing"; model?: string | undefined; missing: string[][] }
  | { state: "error"; model?: string | undefined; latencyMs?: number | undefined; message: string };

// ─── Shared loader for the bot's flow registry ──────────────────────────
// Used by InboundConfigPanel + OutboundConfigPanel. The full FlowsPanel
// further down still loads its own copy independently.
function useFlows() {
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch<FlowsResponse>("/api/settings/flows");
        if (cancelled) return;
        if (data.error) setErr(data.detail ?? data.error);
        setFlows(data.options ?? []);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { flows, loading, err };
}

// ─── Recording on/off toggle for a per-direction setting ────────────────
function RecordingToggle({
  settingKey,
  label,
  hint,
}: {
  settingKey: "inbound_recording_enabled" | "outbound_recording_enabled";
  label: string;
  hint: string;
}) {
  const [value, setValue] = useState<"on" | "off">("on");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/settings/${settingKey}`);
        if (r.status === 404) return;
        const d = (await r.json()) as { value?: string; updated_at?: string };
        if (cancelled) return;
        if (d.value === "on" || d.value === "off") setValue(d.value);
        if (d.updated_at) setSavedAt(d.updated_at);
      } catch {
        /* leave at default 'on' */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [settingKey]);

  async function toggle() {
    const next = value === "on" ? "off" : "on";
    setBusy(true);
    setErr(null);
    try {
      const d = await apiFetch<{ value: string; updated_at: string }>(
        `/api/settings/${settingKey}`,
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ value: next }),
        },
      );
      if (d.value === "on" || d.value === "off") setValue(d.value);
      setSavedAt(d.updated_at);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const enabled = value === "on";
  return (
    <div id={`settings-${settingKey}-row`} className="active-flow-row">
      <div className="active-flow-meta">
        <div className="active-flow-label">{label}</div>
        <div className="active-flow-hint">{hint}</div>
      </div>
      <div className="active-flow-control">
        <label className="setting-toggle">
          <input
            id={`settings-${settingKey}-input`}
            type="checkbox"
            checked={enabled}
            onChange={toggle}
            disabled={busy}
          />
          <span>{enabled ? "On" : "Off"}</span>
        </label>
        {savedAt && !err && (
          <div className="muted active-flow-saved">saved {fmtDateTime(savedAt)}</div>
        )}
        {err && <div id={`settings-${settingKey}-error`} className="dial-err">{err}</div>}
      </div>
    </div>
  );
}

// ─── Numeric input for an integer-valued /api/settings key ────────────
// Originally just for the concurrency caps; widened to take any
// CONFIG.numericSettings key. `initialDraft` is what shows pre-fetch
// (and on a 404) — concurrency caps default to 10, throttle floors
// default to 0 (off). Server is the source of truth otherwise.
function ConcurrencyInput({
  settingKey,
  label,
  hint,
  min,
  max,
  initialDraft = "10",
}: {
  settingKey:
    | "inbound_concurrency_cap"
    | "outbound_concurrency_cap"
    | "agent_desk_card_min_turns"
    | "agent_desk_card_min_interval_seconds";
  label: string;
  hint: string;
  min: number;
  max: number;
  initialDraft?: string;
}) {
  const [value, setValue] = useState<string>(initialDraft);
  const [draft, setDraft] = useState<string>(initialDraft);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/settings/${settingKey}`);
        if (r.status === 404) return;
        const d = (await r.json()) as { value?: string; updated_at?: string };
        if (cancelled) return;
        if (typeof d.value === "string") {
          setValue(d.value);
          setDraft(d.value);
        }
        if (d.updated_at) setSavedAt(d.updated_at);
      } catch {
        /* leave at default 10 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [settingKey]);

  async function commit() {
    if (draft === value) return;
    const n = Number(draft);
    if (!Number.isInteger(n) || n < min || n > max) {
      setErr(`Must be an integer between ${min} and ${max}.`);
      setDraft(value);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const d = await apiFetch<{ value: string; updated_at: string }>(
        `/api/settings/${settingKey}`,
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ value: draft }),
        },
      );
      setValue(d.value);
      setDraft(d.value);
      setSavedAt(d.updated_at);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setDraft(value);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div id={`settings-${settingKey}-row`} className="active-flow-row">
      <div className="active-flow-meta">
        <div className="active-flow-label">{label}</div>
        <div className="active-flow-hint">{hint}</div>
      </div>
      <div className="active-flow-control">
        <input
          id={`settings-${settingKey}-input`}
          className="setting-number"
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={draft}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
        />
        {savedAt && !err && (
          <div className="muted active-flow-saved">saved {fmtDateTime(savedAt)}</div>
        )}
        {err && <div id={`settings-${settingKey}-error`} className="dial-err">{err}</div>}
      </div>
    </div>
  );
}

// ─── Inbound config ─────────────────────────────────────────────────────
// Active flow + recording + concurrency. Phone number + webhook URL stay
// env-derived for now; surfacing them here is a future expansion when the
// worker exposes them via an /api/config/inbound read endpoint.
function InboundConfigPanel() {
  const { flows, loading: flowsLoading } = useFlows();
  const inboundFlows = flows.filter(
    (f) => f.applies_to.includes("inbound") && f.status !== "deprecated",
  );

  return (
    <section id="settings-section-inbound" className="setting-block">
      <p className="setting-intro">
        How inbound retention calls are answered. Active flow, recording,
        and concurrency apply to every call coming in on the receiving
        number.
      </p>

      <div className="active-flows">
        <div className="active-flows-head">CONFIGURATION</div>
        <ActiveFlowSlot
          slot={{
            key: "flow_inbound",
            label: "Active flow",
            appliesTo: "inbound",
            hint: "Runs on every inbound call. Empty = use the legacy persona prompt.",
          }}
          options={inboundFlows}
          loading={flowsLoading}
        />
        <RecordingToggle
          settingKey="inbound_recording_enabled"
          label="Recording"
          hint="When on, every inbound call is recorded via Telnyx and surfaced in the call detail."
        />
        <ConcurrencyInput
          settingKey="inbound_concurrency_cap"
          label="Concurrency cap"
          hint="Max parallel inbound calls the bot will handle (1–100)."
          min={1}
          max={100}
        />
      </div>
    </section>
  );
}

// ─── Agent Desk config ──────────────────────────────────────────────────
// Settings that govern the human-first / overflow-AI listen path
// (the 628 number). Today that's transcription source + advisor card
// throttle; future candidates: ringback timeout, overflow message,
// presence stale window.
function AgentDeskPanel() {
  return (
    <section id="settings-section-agent-desk" className="setting-block">
      <p className="setting-intro">
        How the agent-desk (human-first, AI overflow) listen path is
        configured. Applies to calls that land on the agent-desk number
        and bridge to a human softphone — the bot only takes over on
        overflow.
      </p>

      <div className="active-flows">
        <div className="active-flows-head">CONFIGURATION</div>
        <TranscriptionSourcePicker />
        <ListenSTTBackendPicker />
      </div>

      <div className="active-flows">
        <div className="active-flows-head">ADVISOR CARDS</div>
        <ConcurrencyInput
          settingKey="agent_desk_card_min_turns"
          label="Min turns between cards"
          hint="Suppress new advisor cards until at least this many transcript turns have happened since the last card. 0 = no floor (LLM-driven dedupe only)."
          min={0}
          max={50}
          initialDraft="0"
        />
        <ConcurrencyInput
          settingKey="agent_desk_card_min_interval_seconds"
          label="Min seconds between cards"
          hint="Suppress new advisor cards until at least this many seconds have elapsed since the last card. 0 = no floor. Both floors apply together (AND): a new card needs both turn count AND time to be satisfied."
          min={0}
          max={600}
          initialDraft="0"
        />
      </div>
    </section>
  );
}

// ─── Outbound config ────────────────────────────────────────────────────
// Active flow + recording + concurrency wired to /api/settings. Audience
// builder + Test-outbound are scaffolded as `setting-stub` blocks until
// the next phase lands the rule editor + match-count + dial-one form.
function OutboundConfigPanel() {
  const { flows, loading: flowsLoading } = useFlows();
  const outboundFlows = flows.filter(
    (f) => f.applies_to.includes("outbound") && f.status !== "deprecated",
  );

  return (
    <section id="settings-section-outbound" className="setting-block">
      <p className="setting-intro">
        How the AI runs outbound retention calls. Production outbounds run
        from this config — there's no ad-hoc dial pad.
      </p>

      <div className="active-flows">
        <div className="active-flows-head">CONFIGURATION</div>
        <ActiveFlowSlot
          slot={{
            key: "flow_outbound",
            label: "Active flow",
            appliesTo: "outbound",
            hint: "Runs on every outbound dial. Bot-vs-bot test runs are unaffected.",
          }}
          options={outboundFlows}
          loading={flowsLoading}
        />
        <RecordingToggle
          settingKey="outbound_recording_enabled"
          label="Recording"
          hint="When on, every outbound call is recorded via Telnyx and surfaced in the call detail."
        />
        <ConcurrencyInput
          settingKey="outbound_concurrency_cap"
          label="Concurrency cap"
          hint="Max parallel outbound dials the bot will run (1–100)."
          min={1}
          max={100}
        />
      </div>

      <h3 className="setting-subhead">Audience</h3>
      <AudienceEditor />

      <h3 className="setting-subhead">Test outbound</h3>
      <TestOutbound />
    </section>
  );
}

function ModelPanel() {
  const [active, setActive] = useState<Provider | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState<Provider | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Each provider row registers a handle here so "Test all" can fire them
  // in parallel. We keep both maps separate (realtime vs pipeline) so the
  // two section-level buttons fire only their own rows.
  const realtimeHandlesRef = useRef<Map<TestName, RowTestHandle>>(new Map());
  const pipelineHandlesRef = useRef<Map<TestName, RowTestHandle>>(new Map());
  const makeRegister = useCallback(
    (which: "realtime" | "pipeline") => (h: RowTestHandle | null) => {
      const map = which === "realtime" ? realtimeHandlesRef.current : pipelineHandlesRef.current;
      if (h) map.set(h.testName, h);
    },
    [],
  );
  const realtimeRegister = useMemo(() => makeRegister("realtime"), [makeRegister]);
  const pipelineRegister = useMemo(() => makeRegister("pipeline"), [makeRegister]);
  function testAll(which: "realtime" | "pipeline") {
    const map = which === "realtime" ? realtimeHandlesRef.current : pipelineHandlesRef.current;
    for (const handle of map.values()) handle.run();
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await apiFetch<{ value: string; updated_at: string }>(
          "/api/settings/llm_provider",
        );
        if (cancelled) return;
        setActive(d.value as Provider);
        setSavedAt(d.updated_at);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "load failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function activate(next: Provider) {
    setSaving(next);
    setError(null);
    try {
      const d = await apiFetch<{ value: string; updated_at: string }>(
        "/api/settings/llm_provider",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ value: next }),
        },
      );
      setActive(d.value as Provider);
      setSavedAt(d.updated_at);
    } catch (e) {
      setError(e instanceof Error ? e.message : "save failed");
    } finally {
      setSaving(null);
    }
  }

  return (
    <section id="settings-section-model" className="setting-block">
      <p className="muted">
        Live model lists from each provider · auto-saved · independently
        testable. Applies to the next call; in-flight calls keep the stack
        they started with. Test pings the actual provider API.
      </p>
      {error && <p id="settings-model-error" className="error">Error: {error}</p>}

      {/* Realtime mode — each row is a complete provider, individually activatable. */}
      <div className="provider-mode-block">
        <div className="provider-mode-head provider-mode-head-row">
          <div>
            <h3>Realtime mode</h3>
            <p className="muted small">
              Single multimodal service handles STT, LLM, and TTS in one node.
              Lowest latency; tool-call semantics can be brittle.
            </p>
          </div>
          <button
            id="settings-model-realtime-test-all-btn"
            type="button"
            className="btn-secondary provider-test-all"
            onClick={() => testAll("realtime")}
          >
            Test all
          </button>
        </div>
        <ul className="provider-list">
          {REALTIME_ROWS.map((r) => (
            <RealtimeRowView
              key={r.testName}
              row={r}
              isActive={active === r.activateAs}
              saving={saving === r.activateAs}
              onActivate={() => activate(r.activateAs)}
              registerHandle={realtimeRegister}
            />
          ))}
        </ul>
      </div>

      {/* Pipeline mode — single activation, per-component testing. */}
      <div className="provider-mode-block">
        <div className="provider-mode-head provider-mode-head-row">
          <div>
            <h3>Pipeline mode</h3>
            <p className="muted small">
              STT → LLM → TTS as three services. Higher latency than realtime,
              but tool-calling is rock solid.
            </p>
          </div>
          <div className="provider-mode-actions">
            <button
              id="settings-model-pipeline-test-all-btn"
              type="button"
              className="btn-secondary provider-test-all"
              onClick={() => testAll("pipeline")}
            >
              Test all
            </button>
            {active === "chained" ? (
              <span id="settings-model-pipeline-active-badge" className="provider-active-badge">Active</span>
            ) : (
              // Section-level activate uses the same secondary visual as
              // per-row Activate buttons, so the two don't compete in
              // visual weight. Only the *active* indicator is loud.
              <button
                id="settings-model-pipeline-activate-btn"
                type="button"
                className="btn-secondary"
                onClick={() => activate("chained")}
                disabled={saving === "chained"}
              >
                {saving === "chained" ? "Activating…" : "Activate"}
              </button>
            )}
          </div>
        </div>
        <ul className="provider-list">
          {PIPELINE_ROWS.map((r) => (
            <PipelineRowView
              key={r.testName}
              row={r}
              registerHandle={pipelineRegister}
            />
          ))}
        </ul>
      </div>

      {savedAt && (
        <p className="muted small">Last changed: {fmtDateTime(savedAt)}</p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Test result hook — shared by realtime + pipeline rows. Accepts the
// currently-selected model so the live ping reports against what the
// dropdown shows (not whatever env happens to be set).
//
// Last result is cached in localStorage keyed by testName so the green
// checkmark survives navigating away from /settings/model and back. We
// only cache "ok"/"missing"/"error" — never "running"/"idle".
// ---------------------------------------------------------------------------
const TEST_RESULT_STORAGE_PREFIX = "settings:lastTest:";

function loadCachedResult(testName: TestName): TestResult {
  try {
    const raw = localStorage.getItem(TEST_RESULT_STORAGE_PREFIX + testName);
    if (!raw) return { state: "idle" };
    const parsed = JSON.parse(raw) as TestResult;
    if (parsed.state === "ok" || parsed.state === "missing" || parsed.state === "error") {
      return parsed;
    }
    return { state: "idle" };
  } catch {
    return { state: "idle" };
  }
}

function persistResult(testName: TestName, result: TestResult) {
  try {
    if (result.state === "running" || result.state === "idle") return;
    localStorage.setItem(
      TEST_RESULT_STORAGE_PREFIX + testName,
      JSON.stringify(result),
    );
  } catch {
    /* private mode etc. */
  }
}

function useProviderTest(testName: TestName) {
  const [result, setResult] = useState<TestResult>(() => loadCachedResult(testName));

  // Reset both in-memory state and the localStorage cache. Used when the
  // operator changes the row's model — the prior result was for the old
  // model and is now stale, so showing it would mislead.
  function clear() {
    setResult({ state: "idle" });
    try {
      localStorage.removeItem(TEST_RESULT_STORAGE_PREFIX + testName);
    } catch {
      /* private mode */
    }
  }

  async function run(model?: string) {
    setResult({ state: "running" });
    try {
      type Ok = {
        ok: true;
        model?: string;
        checked: string[];
        latency_ms: number;
      };
      type Bad = {
        ok: false;
        model?: string;
        missing?: string[][];
        error?: string;
        detail?: string;
        latency_ms?: number;
      };
      const qs = model ? `?model=${encodeURIComponent(model)}` : "";
      const r = await apiFetch<Ok | Bad>(
        `/api/settings/test-provider/${testName}${qs}`,
      );
      let next: TestResult;
      if (r.ok) {
        next = {
          state: "ok",
          model: r.model,
          latencyMs: r.latency_ms,
          checked: r.checked,
        };
      } else if (r.missing) {
        next = { state: "missing", model: r.model, missing: r.missing };
      } else {
        next = {
          state: "error",
          model: r.model,
          latencyMs: r.latency_ms,
          message: r.detail || r.error || "unknown error",
        };
      }
      setResult(next);
      persistResult(testName, next);
    } catch (e) {
      const next: TestResult = {
        state: "error",
        message: e instanceof Error ? e.message : "test failed",
      };
      setResult(next);
      persistResult(testName, next);
    }
  }
  return { result, run, clear };
}

// ---------------------------------------------------------------------------
// useModelSetting — fetches the saved model + the option catalogue for one
// settings key. Auto-saves on change (PUT). Designed so each row gets its
// own self-contained dropdown without lifting state up.
// ---------------------------------------------------------------------------
function useModelSetting(key: ModelKey) {
  const [options, setOptions] = useState<ModelOption[]>([]);
  const [value, setValue] = useState<string>("");
  const [source, setSource] = useState<"live" | "cache" | "fallback" | null>(null);
  const [optionsErr, setOptionsErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        type OptsRes = {
          options: ModelOption[];
          source?: "live" | "cache" | "fallback";
          error?: string;
        };
        const [optsRes, valRes] = await Promise.all([
          apiFetch<OptsRes>(`/api/settings/options/${key}`),
          apiFetch<{ value: string }>(`/api/settings/${key}`),
        ]);
        if (cancelled) return;
        setOptions(optsRes.options);
        setSource(optsRes.source ?? null);
        setOptionsErr(optsRes.error ?? null);
        setValue(valRes.value);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "load failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);

  async function save(next: string) {
    const previous = value;
    setValue(next); // optimistic so the dropdown feels instant
    setSaving(true);
    setErr(null);
    try {
      await apiFetch(`/api/settings/${key}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ value: next }),
      });
    } catch (e) {
      setValue(previous);
      setErr(e instanceof Error ? e.message : "save failed");
    } finally {
      setSaving(false);
    }
  }

  return { options, value, save, saving, err, source, optionsErr };
}

function ModelSelect({
  modelKey,
  label,
  className,
}: {
  modelKey: ModelKey;
  label: string;
  className?: string;
}) {
  const { options, value, save, saving, err, source, optionsErr } =
    useModelSetting(modelKey);
  const id = `select-${modelKey}`;
  // Split into "Default / Recommended" and "Other" so the recommended
  // model floats to the top of the picker. Provider catalogues can be
  // long (Gemini ships >50 models); grouping makes scanning bearable.
  const grouped = useMemo(() => {
    if (options.length === 0) return null;
    const recommended = options.filter((o) => o.is_default);
    const rest = options.filter((o) => !o.is_default);
    return { recommended, rest };
  }, [options]);
  return (
    <div className={`model-select ${className ?? ""}`}>
      <label htmlFor={id} className="model-select-label">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => save(e.target.value)}
        disabled={options.length === 0}
        className="model-select-input"
      >
        {options.length === 0 && <option value="">Loading…</option>}
        {grouped && grouped.recommended.length > 0 && (
          <optgroup label="Recommended">
            {grouped.recommended.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
                {o.hint ? ` — ${o.hint}` : ""}
              </option>
            ))}
          </optgroup>
        )}
        {grouped && grouped.rest.length > 0 && (
          <optgroup label={grouped.recommended.length > 0 ? "Other" : "Available"}>
            {grouped.rest.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
                {o.hint ? ` — ${o.hint}` : ""}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      <SourceBadge source={source} optionsErr={optionsErr} />
      {saving && <span className="model-select-saving muted small">saving…</span>}
      {err && <span className="model-select-err">{err}</span>}
    </div>
  );
}

function SourceBadge({
  source,
  optionsErr,
}: {
  source: "live" | "cache" | "fallback" | null;
  optionsErr: string | null;
}) {
  if (!source) return null;
  const cls = source === "fallback" ? "is-fallback" : "is-live";
  const label =
    source === "live"
      ? "live"
      : source === "cache"
        ? "cached"
        : "fallback";
  const title =
    source === "fallback" && optionsErr
      ? `Using fallback list (${optionsErr})`
      : source === "cache"
        ? "Cached (≤5 min old)"
        : "Fetched live from provider";
  return (
    <span className={`model-source-badge ${cls}`} title={title}>
      {label}
    </span>
  );
}

// Imperative handle used by the section-level "Test all" button. Each row
// registers its `run()` so the parent can fire them in parallel.
type RowTestHandle = { testName: TestName; run: () => void };

function RealtimeRowView({
  row,
  isActive,
  saving,
  onActivate,
  registerHandle,
}: {
  row: RealtimeRow;
  isActive: boolean;
  saving: boolean;
  onActivate: () => void;
  registerHandle?: (h: RowTestHandle | null) => void;
}) {
  const { result, run, clear } = useProviderTest(row.testName);
  const model = useModelSetting(row.modelKey);
  // Reset the prior test result when the operator picks a different
  // model — the result was for the old model and is now stale.
  const seededValue = useRef<string | null>(null);
  useEffect(() => {
    if (seededValue.current === null) {
      // First time we see a non-empty value: that's the load, not a change.
      if (model.value) seededValue.current = model.value;
      return;
    }
    if (model.value && model.value !== seededValue.current) {
      seededValue.current = model.value;
      clear();
    }
  }, [model.value, clear]);
  useEffect(() => {
    if (!registerHandle) return;
    registerHandle({
      testName: row.testName,
      run: () => {
        if (model.value) run(model.value);
      },
    });
    return () => registerHandle(null);
  }, [registerHandle, row.testName, run, model.value]);
  return (
    <li
      id={`settings-model-realtime-row-${row.testName}`}
      className={`provider-row ${isActive ? "is-active" : ""}`}
    >
      <div className="provider-row-body">
        <div className="provider-row-name">{row.label}</div>
        <div className="provider-row-sub">{row.sub}</div>
        <ModelSelect modelKey={row.modelKey} label="Model" />
        <ProviderTestResult result={result} requires={row.requires} />
      </div>
      <div className="provider-row-actions">
        <button
          id={`settings-model-realtime-test-btn-${row.testName}`}
          type="button"
          className="btn-secondary"
          onClick={() => run(model.value)}
          disabled={result.state === "running" || !model.value}
        >
          {result.state === "running" ? "Testing…" : "Test"}
        </button>
        {isActive ? (
          <span
            id={`settings-model-realtime-active-badge-${row.testName}`}
            className="provider-active-badge"
          >
            Active
          </span>
        ) : (
          // Match section-level Activate visual: secondary style. The
          // only loud signal is the ACTIVE badge on the currently-on row.
          <button
            id={`settings-model-realtime-activate-btn-${row.testName}`}
            type="button"
            className="btn-secondary"
            onClick={onActivate}
            disabled={saving}
          >
            {saving ? "Activating…" : "Activate"}
          </button>
        )}
      </div>
    </li>
  );
}

function PipelineRowView({
  row,
  registerHandle,
}: {
  row: PipelineRow;
  registerHandle?: (h: RowTestHandle | null) => void;
}) {
  const { result, run, clear } = useProviderTest(row.testName);
  const model = useModelSetting(row.modelKey);
  const seededValue = useRef<string | null>(null);
  useEffect(() => {
    if (seededValue.current === null) {
      if (model.value) seededValue.current = model.value;
      return;
    }
    if (model.value && model.value !== seededValue.current) {
      seededValue.current = model.value;
      clear();
    }
  }, [model.value, clear]);
  useEffect(() => {
    if (!registerHandle) return;
    registerHandle({
      testName: row.testName,
      run: () => {
        if (model.value) run(model.value);
      },
    });
    return () => registerHandle(null);
  }, [registerHandle, row.testName, run, model.value]);
  return (
    <li
      id={`settings-model-pipeline-row-${row.testName}`}
      className="provider-row provider-row-component"
    >
      <div className="provider-row-stage">{row.stage}</div>
      <div className="provider-row-body">
        <div className="provider-row-name">{row.label}</div>
        <ModelSelect modelKey={row.modelKey} label={row.modelLabel} />
        <ProviderTestResult result={result} requires={row.requires} />
      </div>
      <div className="provider-row-actions">
        <button
          id={`settings-model-pipeline-test-btn-${row.testName}`}
          type="button"
          className="btn-secondary"
          onClick={() => run(model.value)}
          disabled={result.state === "running" || !model.value}
        >
          {result.state === "running" ? "Testing…" : "Test"}
        </button>
      </div>
    </li>
  );
}

function ProviderTestResult({
  result,
  requires,
}: {
  result: TestResult;
  requires: string[];
}) {
  if (result.state === "idle") return null;
  if (result.state === "running") {
    return <div className="provider-test-line muted">Pinging provider…</div>;
  }
  if (result.state === "ok") {
    return (
      <div className="provider-test-line provider-test-ok">
        ✓ Live ping ok
        {result.latencyMs !== undefined && ` · ${result.latencyMs} ms`}
        {result.checked.length > 0 && ` · ${result.checked.join(", ")}`}
      </div>
    );
  }
  if (result.state === "missing") {
    const lines = result.missing.map((group) => group.join(" or "));
    return (
      <div className="provider-test-line provider-test-bad">
        ⚠ Missing in pipe-agent env: {lines.join(", ")}
        {requires.length > 0 && (
          <div className="provider-test-hint">
            Expected: {requires.join(", ")}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="provider-test-line provider-test-bad">
      × {result.message}
      {result.latencyMs !== undefined && ` · ${result.latencyMs} ms`}
    </div>
  );
}

// ===========================================================================
// Shared entity-panel scaffolding. Each panel:
//   1. shows a "+ New" button that toggles a create form
//   2. renders a list of rows; each row is either display or edit-form
//   3. delete is soft (server-side) and triggers a list reload
// ===========================================================================
function EntityHeader({
  blurb,
  creating,
  onCreateToggle,
  newLabel,
}: {
  blurb: string;
  creating: boolean;
  onCreateToggle: () => void;
  newLabel: string;
}) {
  // No section title — the page-level H1 already announces it. We only
  // need the description blurb + create button on this row.
  return (
    <div className="entity-head">
      <p className="muted">{blurb}</p>
      <button
        type="button"
        className="entity-new-btn"
        onClick={onCreateToggle}
      >
        {creating ? "Cancel" : `+ ${newLabel}`}
      </button>
    </div>
  );
}

function FormShell({
  title,
  busy,
  err,
  onSubmit,
  onCancel,
  submitLabel,
  children,
}: {
  title: string;
  busy: boolean;
  err: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  submitLabel: string;
  children: ReactNode;
}) {
  // Autofocus the first focusable input + Esc to cancel. Both are
  // user-initiated mounts (clicking + New / Edit) so autofocus is
  // expected, never jarring.
  const formRef = useRef<HTMLFormElement | null>(null);
  useEffect(() => {
    const first = formRef.current?.querySelector<HTMLElement>(
      "input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])",
    );
    first?.focus();
  }, []);

  function onKeyDown(e: React.KeyboardEvent<HTMLFormElement>) {
    if (e.key === "Escape" && !busy) {
      e.preventDefault();
      onCancel();
    }
  }

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      onKeyDown={onKeyDown}
      className="entity-form"
    >
      <div className="entity-form-title">{title}</div>
      {children}
      {err && <div className="error">{err}</div>}
      <div className="entity-form-actions">
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel <kbd className="kbd">Esc</kbd>
        </button>
        <button type="submit" className="entity-submit" disabled={busy}>
          {busy ? "Saving…" : (
            <>
              {submitLabel} <kbd className="kbd kbd-on-accent">Enter</kbd>
            </>
          )}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Customers panel
// ---------------------------------------------------------------------------
type CustomerRow = {
  id: number;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  billing_email: string | null;
  // `plan` is the joined plan name from /api/customers/:id (LEFT JOIN plans).
  // The list endpoint returns plan_id only — so we keep both nullable.
  plan_id: number | null;
  plan?: string | null;
  monthly_charge_cents: number;
  tenure_months: number;
  churn_risk: number;
  status: string;
  notes: string | null;
};

type PlanRow = {
  id: number;
  name: string;
};

type CustomerForm = {
  first_name: string;
  last_name: string;
  phone: string;
  billing_email: string;
  plan_id: number | null;
  monthly_charge_cents: number;
  tenure_months: number;
  churn_risk: number;
  status: string;
  notes: string;
};

const EMPTY_CUSTOMER: CustomerForm = {
  first_name: "",
  last_name: "",
  phone: "",
  billing_email: "",
  plan_id: null,
  monthly_charge_cents: 6500,
  tenure_months: 12,
  churn_risk: 0.5,
  status: "active",
  notes: "",
};

function customerToForm(c: CustomerRow): CustomerForm {
  return {
    first_name: c.first_name ?? "",
    last_name: c.last_name ?? "",
    phone: c.phone,
    billing_email: c.billing_email ?? "",
    plan_id: c.plan_id,
    monthly_charge_cents: c.monthly_charge_cents,
    tenure_months: c.tenure_months,
    churn_risk: c.churn_risk,
    status: c.status,
    notes: c.notes ?? "",
  };
}

function CustomersPanel() {
  const [list, setList] = useState<CustomerRow[]>([]);
  const [planOptions, setPlanOptions] = useState<PlanRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [topErr, setTopErr] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  async function load() {
    try {
      const [c, p] = await Promise.all([
        apiFetch<{ customers: CustomerRow[] }>("/api/customers"),
        apiFetch<{ plans: PlanRow[] }>("/api/plans"),
      ]);
      setList(c.customers);
      setPlanOptions(p.plans);
      setTopErr(null);
    } catch (e) {
      setTopErr(e instanceof Error ? e.message : "load failed");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function remove(id: number) {
    if (!confirm(`Delete subscriber #${id}? They'll be hidden from lists; historical calls stay intact.`))
      return;
    try {
      await apiFetch(`/api/customers/${id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setTopErr(e instanceof Error ? e.message : "delete failed");
    }
  }

  // Filter by name / phone / billing email — case-insensitive substring.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => {
      const name = [c.first_name, c.last_name].filter(Boolean).join(" ").toLowerCase();
      return (
        name.includes(q) ||
        c.phone.toLowerCase().includes(q) ||
        (c.billing_email ?? "").toLowerCase().includes(q)
      );
    });
  }, [list, search]);

  return (
    <section id="settings-section-customers" className="setting-block">
      <EntityHeader
        blurb="Profiles the agent identifies callers as, and that Scenarios role-play."
        creating={creating}
        onCreateToggle={() => setCreating((v) => !v)}
        newLabel="New subscriber"
      />
      {topErr && <p id="settings-customers-error" className="error">Error: {topErr}</p>}

      <div className="entity-search">
        <input
          id="settings-customers-search-input"
          type="search"
          placeholder="Search name, phone, or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search subscribers"
        />
        {list.length > 0 && (
          <span className="entity-search-count">
            {visible.length} of {list.length}
          </span>
        )}
      </div>

      {creating && (
        <CustomerFormView
          initial={EMPTY_CUSTOMER}
          plans={planOptions}
          mode="create"
          onCancel={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await load();
          }}
        />
      )}

      <ul id="settings-customers-list" className="entity-list">
        {visible.length === 0 && list.length > 0 && (
          <li className="entity-row entity-row-empty">
            No subscribers match “{search}”.
          </li>
        )}
        {visible.map((c) => {
          const name =
            [c.first_name, c.last_name].filter(Boolean).join(" ") || c.phone;
          if (editingId === c.id) {
            return (
              <li
                id={`settings-customer-row-${c.id}`}
                key={c.id}
                className="entity-row entity-row-editing"
              >
                <CustomerFormView
                  initial={customerToForm(c)}
                  plans={planOptions}
                  mode="edit"
                  customerId={c.id}
                  onCancel={() => setEditingId(null)}
                  onSaved={async () => {
                    setEditingId(null);
                    await load();
                  }}
                />
              </li>
            );
          }
          return (
            <li id={`settings-customer-row-${c.id}`} key={c.id} className="entity-row">
              <div className="entity-row-body">
                <div className="entity-row-title">
                  #{c.id} · {name}
                </div>
                <div className="entity-row-meta">
                  {fmtPhone(c.phone)} · {c.plan} · ${(c.monthly_charge_cents / 100).toFixed(0)}/mo ·{" "}
                  {c.tenure_months}mo · churn {Math.round(c.churn_risk * 100)}% ·{" "}
                  {c.status}
                </div>
                {c.notes && <div className="entity-row-notes">{c.notes}</div>}
              </div>
              <div className="entity-row-actions">
                <button
                  id={`settings-customer-edit-btn-${c.id}`}
                  type="button"
                  className="btn-secondary"
                  onClick={() => setEditingId(c.id)}
                >
                  Edit
                </button>
                <button
                  id={`settings-customer-delete-btn-${c.id}`}
                  type="button"
                  className="btn-danger"
                  onClick={() => remove(c.id)}
                >
                  Delete
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function CustomerFormView({
  initial,
  plans,
  mode,
  customerId,
  onCancel,
  onSaved,
}: {
  initial: CustomerForm;
  plans: PlanRow[];
  mode: "create" | "edit";
  customerId?: number;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState<CustomerForm>(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body = {
        ...form,
        monthly_charge_cents: Number(form.monthly_charge_cents),
        tenure_months: Number(form.tenure_months),
        churn_risk: Number(form.churn_risk),
      };
      if (mode === "create") {
        await apiFetch("/api/customers", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
        });
      } else if (customerId) {
        await apiFetch(`/api/customers/${customerId}`, {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
        });
      }
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "save failed");
      setBusy(false);
    }
  }

  return (
    <FormShell
      title={mode === "create" ? "New subscriber" : `Edit subscriber #${customerId}`}
      busy={busy}
      err={err}
      onSubmit={submit}
      onCancel={onCancel}
      submitLabel={mode === "create" ? "Create subscriber" : "Save changes"}
    >
      <div className="entity-form-grid">
        <Field label="First name">
          <input
            id="settings-customer-form-first-name"
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          />
        </Field>
        <Field label="Last name">
          <input
            id="settings-customer-form-last-name"
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          />
        </Field>
        <Field label="Phone" hint="E.164 format, e.g. +15555550101">
          <input
            id="settings-customer-form-phone"
            placeholder="+1XXXXXXXXXX"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            required
          />
        </Field>
        <Field label="Plan">
          <select
            id="settings-customer-form-plan"
            value={form.plan_id ?? ""}
            onChange={(e) =>
              setForm({
                ...form,
                plan_id: e.target.value ? Number(e.target.value) : null,
              })
            }
          >
            <option value="">— pick a plan —</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {/* Advanced fields collapsed by default — most subscriber records
          only need name + phone + plan. The other fields drive churn
          modelling and offer eligibility, which most operators won't
          touch on a routine edit. */}
      <FormDisclosure label="Advanced details" defaultOpen={mode === "edit"}>
        <div className="entity-form-grid">
          <Field label="Billing email">
            <input
              id="settings-customer-form-billing-email"
              type="email"
              value={form.billing_email}
              onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
            />
          </Field>
          <Field label="Status">
            <select
              id="settings-customer-form-status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="active">active</option>
              <option value="pending_cancel">pending_cancel</option>
              <option value="cancelled">cancelled</option>
            </select>
          </Field>
          <Field label="Monthly charge ¢">
            <input
              id="settings-customer-form-monthly-charge"
              type="number"
              value={form.monthly_charge_cents}
              min={0}
              step={100}
              onChange={(e) =>
                setForm({ ...form, monthly_charge_cents: Number(e.target.value) })
              }
            />
          </Field>
          <Field label="Tenure (months)">
            <input
              id="settings-customer-form-tenure-months"
              type="number"
              value={form.tenure_months}
              min={0}
              onChange={(e) =>
                setForm({ ...form, tenure_months: Number(e.target.value) })
              }
            />
          </Field>
          <Field label="Churn risk (0-1)">
            <input
              id="settings-customer-form-churn-risk"
              type="number"
              value={form.churn_risk}
              step="0.01"
              min="0"
              max="1"
              onChange={(e) =>
                setForm({ ...form, churn_risk: Number(e.target.value) })
              }
            />
          </Field>
        </div>
        <Field label="Notes" full>
          <textarea
            id="settings-customer-form-notes"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Recent activity, cancellation reason, anything useful"
          />
        </Field>
      </FormDisclosure>
    </FormShell>
  );
}

// ---------------------------------------------------------------------------
// Offers panel
// ---------------------------------------------------------------------------
type OfferRow = {
  id: number;
  code: string;
  name: string;
  description: string;
  discount_percent: number | null;
  discount_cents: number | null;
  free_months: number | null;
  upgrade_to_plan: string | null;
  duration_months: number | null;
  min_tenure_months: number;
  max_tenure_months: number | null;
  min_monthly_charge_cents: number;
  // JSON array of plan ids, e.g. "[1,2]". NULL means all plans eligible.
  eligible_plan_ids: string | null;
  min_churn_risk: number;
  priority: number;
  active: number;
  /** Joined from offer_grants — total times this offer was pitched / accepted. */
  pitched_count: number;
  accepted_count: number;
};

type OfferForm = {
  code: string;
  name: string;
  description: string;
  discount_percent: string;
  discount_cents: string;
  free_months: string;
  upgrade_to_plan: string;
  duration_months: string;
  min_tenure_months: string;
  max_tenure_months: string;
  min_monthly_charge_cents: string;
  // CSV of plan ids in the form (e.g. "1,2"), JSON-array on the wire.
  eligible_plan_ids: string;
  min_churn_risk: string;
  priority: string;
  active: boolean;
};

const EMPTY_OFFER: OfferForm = {
  code: "",
  name: "",
  description: "",
  discount_percent: "",
  discount_cents: "",
  free_months: "",
  upgrade_to_plan: "",
  duration_months: "",
  min_tenure_months: "0",
  max_tenure_months: "",
  min_monthly_charge_cents: "0",
  eligible_plan_ids: "",
  min_churn_risk: "0",
  priority: "0",
  active: true,
};

function offerToForm(o: OfferRow): OfferForm {
  let plansCsv = "";
  if (o.eligible_plan_ids) {
    try {
      const arr = JSON.parse(o.eligible_plan_ids);
      if (Array.isArray(arr)) plansCsv = arr.join(", ");
    } catch {
      /* leave empty on malformed JSON */
    }
  }
  return {
    code: o.code,
    name: o.name,
    description: o.description ?? "",
    discount_percent: o.discount_percent?.toString() ?? "",
    discount_cents: o.discount_cents?.toString() ?? "",
    free_months: o.free_months?.toString() ?? "",
    upgrade_to_plan: o.upgrade_to_plan ?? "",
    duration_months: o.duration_months?.toString() ?? "",
    min_tenure_months: o.min_tenure_months.toString(),
    max_tenure_months: o.max_tenure_months?.toString() ?? "",
    min_monthly_charge_cents: o.min_monthly_charge_cents.toString(),
    eligible_plan_ids: plansCsv,
    min_churn_risk: o.min_churn_risk.toString(),
    priority: o.priority.toString(),
    active: o.active === 1,
  };
}

function offerFormToBody(f: OfferForm): Record<string, unknown> {
  // String → number | null helpers. Empty string = leave unset.
  const numOrNull = (s: string) => (s.trim() === "" ? null : Number(s));
  const num = (s: string) => (s.trim() === "" ? 0 : Number(s));
  const trim = (s: string) => (s.trim() === "" ? null : s.trim());
  // CSV of plan ids → array of numbers. Filter NaN so a stray comma or
  // word doesn't sneak through.
  const planIds = f.eligible_plan_ids
    .split(",")
    .map((p) => Number(p.trim()))
    .filter((n) => Number.isFinite(n));

  return {
    code: f.code.trim(),
    name: f.name.trim(),
    description: f.description,
    discount_percent: numOrNull(f.discount_percent),
    discount_cents: numOrNull(f.discount_cents),
    free_months: numOrNull(f.free_months),
    upgrade_to_plan: trim(f.upgrade_to_plan),
    duration_months: numOrNull(f.duration_months),
    min_tenure_months: num(f.min_tenure_months),
    max_tenure_months: numOrNull(f.max_tenure_months),
    min_monthly_charge_cents: num(f.min_monthly_charge_cents),
    eligible_plan_ids: planIds.length > 0 ? JSON.stringify(planIds) : null,
    min_churn_risk: num(f.min_churn_risk),
    priority: num(f.priority),
    active: f.active ? 1 : 0,
  };
}

function OffersPanel() {
  const [list, setList] = useState<OfferRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [topErr, setTopErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const d = await apiFetch<{ offers: OfferRow[] }>("/api/offers");
      setList(d.offers);
      setTopErr(null);
    } catch (e) {
      setTopErr(e instanceof Error ? e.message : "load failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function remove(id: number) {
    if (!confirm(`Delete offer #${id}? It'll be hidden from new pitches; existing grants stay intact.`))
      return;
    try {
      await apiFetch(`/api/offers/${id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setTopErr(e instanceof Error ? e.message : "delete failed");
    }
  }

  // Pause/Resume — most common ops change. Patches just `active` so the
  // operator never has to open the full edit form to flip the bit.
  // Optimistic update; rolled back on failure.
  async function toggleActive(id: number, currentActive: number) {
    const next = currentActive ? 0 : 1;
    setList((cur) => cur.map((o) => (o.id === id ? { ...o, active: next } : o)));
    setTopErr(null);
    try {
      await apiFetch(`/api/offers/${id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ active: next }),
      });
    } catch (e) {
      setList((cur) => cur.map((o) => (o.id === id ? { ...o, active: currentActive } : o)));
      setTopErr(e instanceof Error ? e.message : "toggle failed");
    }
  }

  return (
    <section id="settings-section-offers" className="setting-block">
      <EntityHeader
        blurb="Catalogue of retention offers the agent pitches via list_offers and make_offer. Eligibility is evaluated at call time against the caller's profile."
        creating={creating}
        onCreateToggle={() => setCreating((v) => !v)}
        newLabel="New offer"
      />
      {topErr && <p id="settings-offers-error" className="error">Error: {topErr}</p>}
      {loading && <p className="muted">Loading…</p>}

      {creating && (
        <OfferFormView
          initial={EMPTY_OFFER}
          mode="create"
          onCancel={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await load();
          }}
        />
      )}

      <ul id="settings-offers-list" className="entity-list">
        {list.map((o, i) => {
          if (editingId === o.id) {
            return (
              <li
                id={`settings-offer-row-${o.id}`}
                key={o.id}
                className="entity-row entity-row-editing"
              >
                <OfferFormView
                  initial={offerToForm(o)}
                  mode="edit"
                  offerId={o.id}
                  onCancel={() => setEditingId(null)}
                  onSaved={async () => {
                    setEditingId(null);
                    await load();
                  }}
                />
              </li>
            );
          }
          // Ordinal pitch label — "Pitched 1st" / "2nd" / "3rd" / "4th"…
          // describes how this offer ranks in the live pitch order. Computed
          // off the array index because list comes back sorted by priority.
          const ordinal = o.active ? ordinalLabel(i + 1) : null;
          return (
            <li
              id={`settings-offer-row-${o.id}`}
              key={o.id}
              className={`entity-row ${o.active ? "" : "is-inactive"}`}
            >
              <div className="entity-row-body">
                <div className="entity-row-title">
                  <code className="offer-code-inline">{o.code}</code> · {o.name}
                  {ordinal && (
                    <span
                      className="offer-priority"
                      title="Pitch order — when multiple offers are eligible, the agent pitches them in this order"
                    >
                      Pitched {ordinal}
                    </span>
                  )}
                  {!o.active && <span className="offer-tag-inactive">inactive</span>}
                  <OfferUsageChip
                    pitched={o.pitched_count}
                    accepted={o.accepted_count}
                  />
                </div>
                <div className="entity-row-meta">{summariseBenefit(o)}</div>
                <div className="entity-row-meta">
                  Eligible: {summariseEligibility(o)}
                </div>
                {o.description && (
                  <div className="entity-row-notes">{o.description}</div>
                )}
              </div>
              <div className="entity-row-actions">
                <button
                  id={`settings-offer-toggle-active-btn-${o.id}`}
                  type="button"
                  className="btn-secondary"
                  onClick={() => toggleActive(o.id, o.active)}
                  title={o.active ? "Pause offer (stops new pitches)" : "Resume offer"}
                >
                  {o.active ? "Pause" : "Resume"}
                </button>
                <button
                  id={`settings-offer-edit-btn-${o.id}`}
                  type="button"
                  className="btn-secondary"
                  onClick={() => setEditingId(o.id)}
                >
                  Edit
                </button>
                <button
                  id={`settings-offer-delete-btn-${o.id}`}
                  type="button"
                  className="btn-danger"
                  onClick={() => remove(o.id)}
                >
                  Delete
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function OfferUsageChip({ pitched, accepted }: { pitched: number; accepted: number }) {
  if (pitched === 0) {
    return (
      <span className="offer-usage offer-usage-empty" title="Never pitched yet">
        Unused
      </span>
    );
  }
  const acceptRate = accepted > 0 ? Math.round((accepted / pitched) * 100) : 0;
  return (
    <span
      className="offer-usage"
      title={`Pitched ${pitched} time${pitched === 1 ? "" : "s"} · accepted ${accepted}`}
    >
      {pitched} pitched
      {accepted > 0 && <span className="offer-usage-rate"> · {acceptRate}% accepted</span>}
    </span>
  );
}

function ordinalLabel(n: number): string {
  // Standard English ordinals; covers all positions we'd realistically see
  // in a curated offer catalogue (rarely more than ~10 active at once).
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

function OfferFormView({
  initial,
  mode,
  offerId,
  onCancel,
  onSaved,
}: {
  initial: OfferForm;
  mode: "create" | "edit";
  offerId?: number;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState<OfferForm>(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body = offerFormToBody(form);
      if (mode === "create") {
        await apiFetch("/api/offers", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
        });
      } else if (offerId) {
        await apiFetch(`/api/offers/${offerId}`, {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
        });
      }
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "save failed");
      setBusy(false);
    }
  }

  return (
    <FormShell
      title={mode === "create" ? "New offer" : `Edit offer #${offerId}`}
      busy={busy}
      err={err}
      onSubmit={submit}
      onCancel={onCancel}
      submitLabel={mode === "create" ? "Create offer" : "Save changes"}
    >
      <FieldGroup label="Identity">
        <div className="entity-form-grid">
          <Field label="Code" hint="Stable id the agent passes to make_offer.">
            <input
              id="settings-offer-form-code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="LOYALTY_25"
              required
            />
          </Field>
          <Field label="Name" hint="Caller-facing name; used in transcripts.">
            <input
              id="settings-offer-form-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="25% loyalty discount"
              required
            />
          </Field>
          <Field label="Priority" hint="Higher = pitched first when multiple eligible.">
            <input
              id="settings-offer-form-priority"
              type="number"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
            />
          </Field>
          <Field label="Active">
            <label className="entity-checkbox">
              <input
                id="settings-offer-form-active"
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              <span>{form.active ? "Yes" : "No"}</span>
            </label>
          </Field>
        </div>
        <Field
          label="Description (caller-facing)"
          full
          hint="The agent says this when pitching. Keep it conversational."
        >
          <textarea
            id="settings-offer-form-description"
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What the agent will say to the caller."
          />
        </Field>
      </FieldGroup>

      <FieldGroup label="Benefit">
        <div className="entity-form-grid">
          <Field label="Discount %" hint="Percent off MRR. Mutually exclusive with discount ¢.">
            <input
              id="settings-offer-form-discount-percent"
              type="number"
              value={form.discount_percent}
              onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
            />
          </Field>
          <Field label="Discount ¢" hint="Flat amount off in cents.">
            <input
              id="settings-offer-form-discount-cents"
              type="number"
              value={form.discount_cents}
              onChange={(e) => setForm({ ...form, discount_cents: e.target.value })}
            />
          </Field>
          <Field label="Free months" hint="Whole-month grace, e.g. 1 = next bill skipped.">
            <input
              id="settings-offer-form-free-months"
              type="number"
              value={form.free_months}
              onChange={(e) => setForm({ ...form, free_months: e.target.value })}
            />
          </Field>
          <Field label="Upgrade to plan" hint="Plan name to upgrade caller to.">
            <input
              id="settings-offer-form-upgrade-to-plan"
              value={form.upgrade_to_plan}
              onChange={(e) =>
                setForm({ ...form, upgrade_to_plan: e.target.value })
              }
              placeholder="unlimited"
            />
          </Field>
          <Field label="Duration (months)" hint="How long the benefit lasts. Blank = lifetime.">
            <input
              id="settings-offer-form-duration-months"
              type="number"
              value={form.duration_months}
              onChange={(e) =>
                setForm({ ...form, duration_months: e.target.value })
              }
            />
          </Field>
        </div>
      </FieldGroup>

      {/* Eligibility rules are evaluated at call time; most edits don't
          touch them, so collapse by default in create mode. */}
      <FormDisclosure label="Eligibility rules" defaultOpen={mode === "edit"}>
        <div className="entity-form-grid">
          <Field label="Min tenure (months)" hint="Caller must be at least this many months in.">
            <input
              id="settings-offer-form-min-tenure-months"
              type="number"
              value={form.min_tenure_months}
              onChange={(e) =>
                setForm({ ...form, min_tenure_months: e.target.value })
              }
            />
          </Field>
          <Field label="Max tenure (months)" hint="Caller must be no more than this many months in.">
            <input
              id="settings-offer-form-max-tenure-months"
              type="number"
              value={form.max_tenure_months}
              onChange={(e) =>
                setForm({ ...form, max_tenure_months: e.target.value })
              }
              placeholder="(no limit)"
            />
          </Field>
          <Field label="Min monthly charge ¢" hint="Caller's MRR must be at least this much.">
            <input
              id="settings-offer-form-min-monthly-charge"
              type="number"
              value={form.min_monthly_charge_cents}
              onChange={(e) =>
                setForm({ ...form, min_monthly_charge_cents: e.target.value })
              }
            />
          </Field>
          <Field label="Min churn risk (0-1)" hint="Only pitch if caller's churn score ≥ this.">
            <input
              id="settings-offer-form-min-churn-risk"
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={form.min_churn_risk}
              onChange={(e) =>
                setForm({ ...form, min_churn_risk: e.target.value })
              }
            />
          </Field>
        </div>
        <Field
          label="Eligible plan ids"
          full
          hint="Comma-separated plan ids the caller's plan must match. Blank = any plan."
        >
          <input
            id="settings-offer-form-eligible-plan-ids"
            value={form.eligible_plan_ids}
            onChange={(e) =>
              setForm({ ...form, eligible_plan_ids: e.target.value })
            }
            placeholder="2, 3"
          />
        </Field>
      </FormDisclosure>
    </FormShell>
  );
}

// ---------------------------------------------------------------------------
// Tiny field primitives
// ---------------------------------------------------------------------------
function Field({
  label,
  full,
  hint,
  children,
}: {
  label: string;
  full?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className={`entity-field ${full ? "is-full" : ""}`}>
      <span className="entity-field-label">{label}</span>
      {children}
      {hint && <span className="entity-field-hint">{hint}</span>}
    </label>
  );
}

function FieldGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <fieldset className="entity-fieldgroup">
      <legend>{label}</legend>
      {children}
    </fieldset>
  );
}

/**
 * Collapsible section inside a form. Used to hide rarely-edited "advanced"
 * fields under a single click so the create/edit form stays scannable.
 * Defaults to open in edit mode (so existing values are visible) and
 * closed in create mode.
 */
function FormDisclosure({
  label,
  defaultOpen = false,
  children,
}: {
  label: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`entity-disclosure ${open ? "is-open" : ""}`}>
      <button
        type="button"
        className="entity-disclosure-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="entity-disclosure-caret" aria-hidden>
          {open ? "▾" : "▸"}
        </span>
        {label}
      </button>
      {open && <div className="entity-disclosure-body">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
function summariseBenefit(o: OfferRow): string {
  const parts: string[] = [];
  if (o.discount_percent != null) parts.push(`${o.discount_percent}% off`);
  if (o.discount_cents != null) parts.push(`$${(o.discount_cents / 100).toFixed(0)} off`);
  if (o.free_months != null) parts.push(`${o.free_months} free month${o.free_months === 1 ? "" : "s"}`);
  if (o.upgrade_to_plan) parts.push(`upgrade → ${o.upgrade_to_plan}`);
  if (o.duration_months != null) parts.push(`for ${o.duration_months}mo`);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

function summariseEligibility(o: OfferRow): string {
  const parts: string[] = [];
  if (o.min_tenure_months > 0) parts.push(`≥ ${o.min_tenure_months}mo tenure`);
  if (o.max_tenure_months != null) parts.push(`≤ ${o.max_tenure_months}mo tenure`);
  if (o.min_monthly_charge_cents > 0) {
    parts.push(`bill ≥ $${(o.min_monthly_charge_cents / 100).toFixed(0)}/mo`);
  }
  if (o.min_churn_risk > 0) parts.push(`churn ≥ ${Math.round(o.min_churn_risk * 100)}%`);
  if (o.eligible_plan_ids) {
    try {
      const ids = JSON.parse(o.eligible_plan_ids) as number[];
      if (Array.isArray(ids) && ids.length > 0) {
        parts.push(`plan ids: ${ids.join(", ")}`);
      }
    } catch {
      /* ignore malformed JSON */
    }
  }
  return parts.length > 0 ? parts.join(" · ") : "any caller";
}

// ---------------------------------------------------------------------------
// Flows panel — read-only catalogue of every conversation flow registered
// on the bot. Selection of an active flow per direction lands once the
// runtime FlowManager is wired; this panel exists today so operators can
// see what's in flight and at what status.
// ---------------------------------------------------------------------------
type FlowStatus = "draft" | "review" | "active" | "deprecated";

type FlowSummary = {
  id: string;
  name: string;
  version: string;
  status: FlowStatus;
  applies_to: string[];
  when_it_runs: string;
  out_of_scope: string;
  success_metric: string;
  business_owner: string | null;
  engineering_owner: string | null;
  agent_name: string;
  tone: string;
  greeting: string;
  closing: string;
  tools: { name: string }[];
  nodes: {
    id: string;
    goal: string;
    max_turns: number;
    // Pairs of [next_node_id, condition]. Drives FlowGraph edges.
    exits: Array<[string, string]>;
  }[];
  entry_node: string;
  decision_logic: string;
  guardrails: string;
};

type FlowsResponse = {
  source?: string;
  options?: FlowSummary[];
  error?: string;
  detail?: string;
};

function FlowsPanel() {
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch<FlowsResponse>("/api/settings/flows");
        if (cancelled) return;
        if (data.error) {
          setErr(data.detail ?? data.error);
        }
        setFlows(data.options ?? []);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="settings-section-flows" className="setting-block">
      <h2>Flows</h2>
      <p className="setting-intro">
        Conversation flows registered on the bot. Each flow captures one use case —
        identity, persona, tools, and node graph. Status reflects whether the
        runtime is wired (<code>active</code>) or the spec is still in flight
        (<code>draft</code> / <code>review</code>).
      </p>

      <ActiveFlowsPicker flows={flows} loading={loading} />

      {loading && <div className="muted">Loading flows…</div>}
      {!loading && err && (
        <div id="settings-flows-error" className="dial-err">
          {err}
        </div>
      )}
      {!loading && flows.length === 0 && !err && (
        <div className="muted">No flows registered yet.</div>
      )}

      {flows.length > 0 && (
        <>
          <h3 className="setting-subhead">Catalogue</h3>
          <div id="settings-flows-table" className="flow-table">
            <div className="flow-table-head">
              <span>Flow</span>
              <span>Version</span>
              <span>Applies to</span>
              <span>Nodes</span>
              <span>Status</span>
              <span aria-hidden></span>
            </div>
            {flows.map((f) => (
              <div id={`settings-flow-row-${f.id}`} key={f.id} className="flow-row">
                <Link
                  id={`settings-flow-link-${f.id}`}
                  href={`/flows/${f.id}`}
                  className="flow-row-summary is-link"
                >
                  <span className="flow-row-name">
                    <strong>{f.name}</strong>
                    <span className="flow-row-id">{f.id}</span>
                  </span>
                  <span>{f.version}</span>
                  <span>{f.applies_to.join(", ")}</span>
                  <span>{f.nodes.length}</span>
                  <span>
                    <FlowStatusBadge status={f.status} />
                  </span>
                  <span className="flow-row-cta">Review →</span>
                </Link>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

// ---- Active flow picker --------------------------------------------------
// Top-of-panel selector: which registered flow is active for inbound vs.
// outbound calls. Empty value === legacy persona prompt (the historical
// behaviour). Validation against the registry happens server-side; we just
// post the chosen id and surface server errors inline.
type FlowSlot = {
  key: "flow_inbound" | "flow_outbound";
  label: string;
  appliesTo: "inbound" | "outbound";
  hint: string;
};

const FLOW_SLOTS: FlowSlot[] = [
  {
    key: "flow_inbound",
    label: "Inbound calls",
    appliesTo: "inbound",
    hint: "Runs on every inbound call. Empty = use the legacy persona prompt.",
  },
  {
    key: "flow_outbound",
    label: "Outbound calls",
    appliesTo: "outbound",
    hint: "Runs on every outbound /dial. Bot-vs-bot test runs are unaffected.",
  },
];

function ActiveFlowsPicker({
  flows,
  loading,
}: {
  flows: FlowSummary[];
  loading: boolean;
}) {
  return (
    <div className="active-flows">
      <div className="active-flows-head">ACTIVE</div>
      <RuntimePicker />
      {FLOW_SLOTS.map((slot) => (
        <ActiveFlowSlot
          key={slot.key}
          slot={slot}
          // Deprecated flows are filtered out — D1 may still hold a stale id,
          // but we don't want operators to *select* one going forward.
          options={flows.filter(
            (f) => f.applies_to.includes(slot.appliesTo) && f.status !== "deprecated",
          )}
          loading={loading}
        />
      ))}
    </div>
  );
}

// Runtime picker — selects which executor handles a selected flow at call
// start. Default 'prompt' compiles the spec into a single system prompt
// (v1). 'flow_manager' uses pipecat-flows for per-node task messages, tool
// gating, and edge-function transitions. Stored at /api/settings/flow_runtime.
type FlowRuntime = "prompt" | "flow_manager";

const RUNTIME_LABEL: Record<FlowRuntime, string> = {
  prompt: "Prompt (v1)",
  flow_manager: "FlowManager (v2)",
};

function RuntimePicker() {
  const [value, setValue] = useState<FlowRuntime>("prompt");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/settings/flow_runtime");
        if (r.status === 404) return; // unset → leave at default 'prompt'
        const d = (await r.json()) as { value?: string; updated_at?: string };
        if (cancelled) return;
        if (d.value === "prompt" || d.value === "flow_manager") setValue(d.value);
        if (d.updated_at) setSavedAt(d.updated_at);
      } catch {
        /* server may be down — leave at default */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function save(next: FlowRuntime) {
    setBusy(true);
    setErr(null);
    try {
      const d = await apiFetch<{ value: string; updated_at: string }>(
        "/api/settings/flow_runtime",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ value: next }),
        },
      );
      setValue(d.value as FlowRuntime);
      setSavedAt(d.updated_at);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div id="settings-flow-runtime-row" className="active-flow-row">
      <div className="active-flow-meta">
        <div className="active-flow-label">Runtime</div>
        <div className="active-flow-hint">
          How an active flow is executed. <code>prompt</code> compiles the
          whole spec into one system prompt; <code>flow_manager</code> uses
          per-node messages, tool gating, and edge-function transitions.
          Applies to the next call.
        </div>
      </div>
      <div className="active-flow-control">
        <select
          id="settings-flow-runtime-select"
          className="model-select-input"
          disabled={busy}
          value={value}
          onChange={(e) => save(e.target.value as FlowRuntime)}
        >
          <option value="prompt">{RUNTIME_LABEL.prompt}</option>
          <option value="flow_manager">{RUNTIME_LABEL.flow_manager}</option>
        </select>
        {savedAt && !err && (
          <div className="muted active-flow-saved">saved {fmtDateTime(savedAt)}</div>
        )}
        {err && <div id="settings-flow-runtime-error" className="dial-err">{err}</div>}
      </div>
    </div>
  );
}

// Transcription-source picker — chooses how the agent-desk listen path
// gets transcripts. "relay" = Telnyx forks audio to /ws-listen, our
// listen_bridge forwards bytes to Deepgram (we own the STT session,
// keywords/keywords/track tagging fully under our control). "telnyx" =
// TeXML <Transcription> verb, Telnyx runs STT internally and POSTs to
// /twiml/transcription (less control, fewer Deepgram knobs, simpler infra).
// Stored at /api/settings/transcription_source. Default "relay".
type TranscriptionSource = "relay" | "telnyx";

const TRANSCRIPTION_SOURCE_LABEL: Record<TranscriptionSource, string> = {
  relay:  "Relay (our bridge → Deepgram)",
  telnyx: "Telnyx (managed STT)",
};

function TranscriptionSourcePicker() {
  const [value, setValue] = useState<TranscriptionSource>("relay");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/settings/transcription_source");
        if (r.status === 404) return;
        const d = (await r.json()) as { value?: string; updated_at?: string };
        if (cancelled) return;
        if (d.value === "relay" || d.value === "telnyx") setValue(d.value);
        if (d.updated_at) setSavedAt(d.updated_at);
      } catch {
        /* server may be down — leave at default */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function save(next: TranscriptionSource) {
    setBusy(true);
    setErr(null);
    try {
      const d = await apiFetch<{ value: string; updated_at: string }>(
        "/api/settings/transcription_source",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ value: next }),
        },
      );
      setValue(d.value as TranscriptionSource);
      setSavedAt(d.updated_at);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div id="settings-transcription-source-row" className="active-flow-row">
      <div className="active-flow-meta">
        <div className="active-flow-label">Transcription source</div>
        <div className="active-flow-hint">
          How the listen path gets transcripts.{" "}
          <code>relay</code> uses our Deepgram bridge (full tuning + per-track
          attribution). <code>telnyx</code> uses Telnyx-managed STT
          (simpler, fewer knobs, track attribution unverified). Applies on
          the next agent-desk call.
        </div>
      </div>
      <div className="active-flow-control">
        <select
          id="settings-transcription-source-select"
          className="model-select-input"
          disabled={busy}
          value={value}
          onChange={(e) => save(e.target.value as TranscriptionSource)}
        >
          <option value="relay">{TRANSCRIPTION_SOURCE_LABEL.relay}</option>
          <option value="telnyx">{TRANSCRIPTION_SOURCE_LABEL.telnyx}</option>
        </select>
        {savedAt && !err && (
          <div className="muted active-flow-saved">saved {fmtDateTime(savedAt)}</div>
        )}
        {err && <div id="settings-transcription-source-error" className="dial-err">{err}</div>}
      </div>
    </div>
  );
}

// Listen-bridge STT backend picker — only meaningful when transcription_source
// is "relay". Chooses between Deepgram (default, nova-2-phonecall) and
// OpenAI's gpt-realtime-whisper transcription session. Stored at
// /api/settings/listen_stt_backend; the bot reads it at /ws-listen handshake
// and applies on the next agent-desk call.
type ListenSTTBackend = "deepgram" | "openai_whisper";

const LISTEN_STT_BACKEND_LABEL: Record<ListenSTTBackend, string> = {
  deepgram:       "Deepgram (nova-2-phonecall)",
  openai_whisper: "OpenAI (gpt-realtime-whisper)",
};

function ListenSTTBackendPicker() {
  const [value, setValue] = useState<ListenSTTBackend>("deepgram");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/settings/listen_stt_backend");
        if (r.status === 404) return;
        const d = (await r.json()) as { value?: string; updated_at?: string };
        if (cancelled) return;
        if (d.value === "deepgram" || d.value === "openai_whisper") {
          setValue(d.value);
        }
        if (d.updated_at) setSavedAt(d.updated_at);
      } catch {
        /* server may be down — leave at default */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function save(next: ListenSTTBackend) {
    setBusy(true);
    setErr(null);
    try {
      const d = await apiFetch<{ value: string; updated_at: string }>(
        "/api/settings/listen_stt_backend",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ value: next }),
        },
      );
      setValue(d.value as ListenSTTBackend);
      setSavedAt(d.updated_at);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div id="settings-listen-stt-backend-row" className="active-flow-row">
      <div className="active-flow-meta">
        <div className="active-flow-label">Relay STT backend</div>
        <div className="active-flow-hint">
          Which STT engine the relay bridge uses (only applies when
          transcription source is <code>relay</code>). Deepgram is the
          long-running default. <code>openai_whisper</code> opens one
          OpenAI Realtime transcription session per Telnyx track and runs{" "}
          <code>gpt-realtime-whisper</code>; roughly 3× the per-minute cost
          of Deepgram. Applies on the next agent-desk call.
        </div>
      </div>
      <div className="active-flow-control">
        <select
          id="settings-listen-stt-backend-select"
          className="model-select-input"
          disabled={busy}
          value={value}
          onChange={(e) => save(e.target.value as ListenSTTBackend)}
        >
          <option value="deepgram">{LISTEN_STT_BACKEND_LABEL.deepgram}</option>
          <option value="openai_whisper">
            {LISTEN_STT_BACKEND_LABEL.openai_whisper}
          </option>
        </select>
        {savedAt && !err && (
          <div className="muted active-flow-saved">saved {fmtDateTime(savedAt)}</div>
        )}
        {err && (
          <div id="settings-listen-stt-backend-error" className="dial-err">{err}</div>
        )}
      </div>
    </div>
  );
}

function ActiveFlowSlot({
  slot,
  options,
  loading,
}: {
  slot: FlowSlot;
  options: FlowSummary[];
  loading: boolean;
}) {
  const [value, setValue] = useState<string>("");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/settings/${slot.key}`);
        if (r.status === 404) return; // unset
        const d = (await r.json()) as { value?: string; updated_at?: string };
        if (cancelled) return;
        if (typeof d.value === "string") setValue(d.value);
        if (d.updated_at) setSavedAt(d.updated_at);
      } catch {
        /* server may be down — leave at default empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slot.key]);

  async function save(next: string) {
    setBusy(true);
    setErr(null);
    try {
      const d = await apiFetch<{ value: string; updated_at: string }>(
        `/api/settings/${slot.key}`,
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ value: next }),
        },
      );
      setValue(d.value);
      setSavedAt(d.updated_at);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const selected = options.find((o) => o.id === value);

  return (
    <div id={`settings-active-flow-row-${slot.key}`} className="active-flow-row">
      <div className="active-flow-meta">
        <div className="active-flow-label">{slot.label}</div>
        <div className="active-flow-hint">{slot.hint}</div>
      </div>
      <div className="active-flow-control">
        <select
          id={`settings-active-flow-select-${slot.key}`}
          className="model-select-input"
          disabled={loading || busy}
          value={value}
          onChange={(e) => save(e.target.value)}
        >
          <option value="">— Legacy persona —</option>
          {options.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name} (v{f.version} · {f.status})
            </option>
          ))}
        </select>
        {selected && (
          <div className="active-flow-detail">
            <FlowStatusBadge status={selected.status} />
            <span className="muted">{selected.id}</span>
          </div>
        )}
        {savedAt && !err && (
          <div className="muted active-flow-saved">saved {fmtDateTime(savedAt)}</div>
        )}
        {err && <div id={`settings-active-flow-error-${slot.key}`} className="dial-err">{err}</div>}
      </div>
    </div>
  );
}

function FlowStatusBadge({ status }: { status: FlowStatus }) {
  return <span className={`flow-status flow-status-${status}`}>{status}</span>;
}

// FlowDetail / FlowGraph removed from Settings → Flows. The full review
// now lives at /flows/:id (apps/web/src/FlowReview.tsx) where it has the
// width to render the graph properly.

// ---------------------------------------------------------------------------
// About panel — what's deployed right now. Sourced live from /api/version
// so the values reflect whatever the API + bot workers were last shipped
// with, not whatever the browser bundle thinks. Replaces the old top-right
// build chip in AppShell.
// ---------------------------------------------------------------------------
type VersionResponse = {
  version: string;
  commit: string;
  built_at: string | null;
  env: string;
};

function AboutPanel() {
  const [data, setData] = useState<VersionResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await apiFetch<VersionResponse>("/api/version");
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="settings-section-about" className="setting-block">
      <h2>About</h2>
      <p className="setting-intro">
        Build identity for the deployed API + bot. Live from <code>/api/version</code>.
      </p>

      {err && (
        <div id="settings-about-error" className="dial-err">
          {err}
        </div>
      )}

      {!data && !err && <div className="muted">Loading…</div>}

      {data && (
        <dl className="about-meta">
          <dt>Version</dt>
          <dd>
            <code className="about-mono">{data.version}</code>
          </dd>
          <dt>Commit</dt>
          <dd>
            <code className="about-mono">{data.commit}</code>
          </dd>
          <dt>Built</dt>
          <dd>{data.built_at ? fmtDateTime(data.built_at) : "—"}</dd>
          <dt>Environment</dt>
          <dd>
            <code className="about-mono">{data.env}</code>
          </dd>
        </dl>
      )}
    </section>
  );
}
